import React, { FC, useState } from "react";
import { GetStaticProps } from "next";
import { cmsPageProps } from "../lib/pageProps";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import {
  Close,
  Cottage,
  Diversity3,
  MenuBook,
  CheckCircle,
  NavigateBefore,
  NavigateNext,
  Payments,
  WorkOutline,
  WorkspacePremium,
} from "@mui/icons-material";
import { Card, Grid, IconButton, Modal, styled } from "@mui/material";
import BreadcrumbsComponent from "../components/_shared/Breadcrumbs";
import CustomButton from "../components/_shared/CustomButton";
import AppContainer from "../layouts/AppContainer";
import Meta from "../layouts/Meta";

const WHATSAPP_URL = "https://wa.me/60193324731?text=SOLS%20ASLI%20Women%20Academy";
const IMG = "/assets/images/sawa";

const SawaPage: FC = () => {
  const { t } = useTranslation("sawa");
  const router = useRouter();
  const url = router.pathname;
  const brandName = t("meta-keyword");
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);

  const benefits = [
    { key: "why-card1", Icon: WorkspacePremium },
    { key: "why-card2", Icon: Cottage },
    { key: "why-card3", Icon: WorkOutline },
    { key: "why-card4", Icon: Diversity3 },
    { key: "why-card5", Icon: Payments },
    { key: "why-card6", Icon: MenuBook },
  ] as const;

  const subjectKeys = [
    "subject1",
    "subject2",
    "subject3",
    "subject4",
    "subject5",
    "subject6",
    "subject7",
    "subject8",
  ];

  const phases = [
    {
      num: "01",
      duration: t("plan-p1-duration"),
      title: t("plan-p1-title"),
      desc: t("plan-p1-desc"),
      allowance: t("plan-p1-allowance"),
      label: t("plan-p1-allowance-label"),
    },
    {
      num: "02",
      duration: t("plan-p2-duration"),
      title: t("plan-p2-title"),
      desc: t("plan-p2-desc"),
      allowance: t("plan-p2-allowance"),
      label: t("plan-p2-allowance-label"),
    },
    {
      num: "03",
      duration: t("plan-p3-duration"),
      title: t("plan-p3-title"),
      desc: t("plan-p3-desc"),
      allowance: t("plan-p3-allowance"),
      label: t("plan-p3-allowance-label"),
    },
    {
      num: "04",
      duration: t("plan-p4-duration"),
      title: t("plan-p4-title"),
      desc: t("plan-p4-desc"),
      allowance: t("plan-p4-allowance"),
      label: t("plan-p4-allowance-label"),
    },
  ];

  const criteria = [
    { key: "criteria1" },
    { key: "criteria2" },
    { key: "criteria3" },
    { key: "criteria4" },
    { key: "criteria5", note: "criteria5-note" },
    { key: "criteria6" },
  ] as const;

  const ctaTicks = ["cta-tick1", "cta-tick2", "cta-tick3"];

  const gallery = Array.from(
    { length: 15 },
    (_, i) => `${IMG}/gallery-${String(i + 1).padStart(2, "0")}.webp`,
  );
  // Collage feel: a few tiles span 2 rows / 2 columns (like the reference).
  const gallerySpan = (i: number) => {
    if ([0, 6, 8].includes(i)) return "spanTall";
    if ([3, 11].includes(i)) return "spanWide";
    return "";
  };

  const press = [
    {
      name: "Juice",
      logo: "https://cdn.solssmart.org/static/press/juice.png",
      url: "https://juiceonline.com/sols-asli-women-academy/",
    },
    {
      name: "SAYS",
      logo: "https://cdn.solssmart.org/static/press/says.png",
      url: "https://says.com/my/lifestyle/this-programme-turns-orang-asli-women-into-paid-teachers-in-their-own-villages",
    },
    {
      name: "The Star",
      logo: "https://cdn.solssmart.org/static/press/the-star-online.png",
      url: "https://www.thestar.com.my/metro/metro-news/2026/04/09/new-scheme-trains-orang-asli-women-to-be-educators-leaders-in-their-own-communities",
    },
    {
      name: "The Sun Daily",
      logo: "https://cdn.solssmart.org/static/press/the-sun-daily.png",
      url: "https://thesun-ipaper.cld.bz/20260513/4/",
    },
    {
      name: "Eco-Business",
      logo: "https://cdn.solssmart.org/static/press/eco-business.png",
      url: "https://www.eco-business.com/opinion/sustainable-development-in-indigenous-communities-must-start-with-local-women/",
    },
  ];

  const ctaButtonSx = {
    "&.MuiButton-sizeMedium": { px: 4 },
    whiteSpace: "nowrap",
    width: "auto",
    maxWidth: "none",
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "EducationalOccupationalProgram",
    name: t("meta-keyword"),
    url: "https://sols.foundation/sols-asli-women-academy-sawa",
    description: t("meta-description"),
    provider: { "@id": "https://sols.foundation/#organization" },
    programPrerequisites: criteria.map((c) => t(c.key)),
    timeToComplete: "P1Y",
    occupationalCategory: ["Educator", "Community Teacher"],
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "MYR",
      availability: "https://schema.org/InStock",
    },
  };
  const schemaJson = JSON.stringify(schemaData).replace(/</g, "\\u003c");

  const openLightbox = (index: number) => setActiveImageIndex(index);
  const closeLightbox = () => setActiveImageIndex(null);
  const showPrev = () =>
    setActiveImageIndex((current) =>
      current === null
        ? current
        : (current - 1 + gallery.length) % gallery.length,
    );
  const showNext = () =>
    setActiveImageIndex((current) =>
      current === null ? current : (current + 1) % gallery.length,
    );

  return (
    <AppContainer>
      <Meta
        title={t("meta-keyword")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
        image="https://sols.foundation/assets/images/sawa/hero.webp"
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger -- JSON-LD injection for SEO schema
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />

      <BreadcrumbsComponent url={url} title={brandName} />

      <Root>
        {/* Hero */}
        <section className="hero">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={8}>
              <h1 className="heroBrand">{brandName}</h1>
              <p className="heroTitle">
                <span className="heroAccent">{t("title-1")}</span>
                <br />
                {t("title-2")}
              </p>
              <p className="heroIntro">{t("intro-p")}</p>
              <ul className="heroTags">
                {["hero-tag1", "hero-tag2", "hero-tag3"].map((k) => (
                  <li key={k}>
                    <CheckCircle className="heroTagIcon" />
                    {t(k)}
                  </li>
                ))}
              </ul>
              <div className="ctaRow">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <CustomButton sx={ctaButtonSx}>{t("hero-cta")}</CustomButton>
                </a>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <img
                src={`${IMG}/hero.webp`}
                alt={`${brandName} participants`}
                className="heroImage"
              />
            </Grid>
          </Grid>
        </section>

        {/* Why Should You Join SAWA? */}
        <section className="band bandWhite">
          <div className="bandInner">
            <h2 className="centerHeading">{t("why-h2")}</h2>
            <Grid container spacing={2} justifyContent="center">
              {benefits.map(({ key, Icon }) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={key}
                  className="whyGridItem"
                >
                  <Card className="whyCard">
                    <Icon className="whyIcon" />
                    <h3>{t(key)}</h3>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <p className="urgencyText">{t("why-cta-line")}</p>
            <div className="ctaRow centerRow">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <CustomButton sx={ctaButtonSx}>{t("why-cta")}</CustomButton>
              </a>
            </div>
          </div>
        </section>

        {/* Life In SAWA Programme */}
        <section className="band bandPeach">
          <div className="bandInner">
            <h2 className="centerHeading accentHeading">{t("life-h2")}</h2>
            <div className="galleryGrid">
              {gallery.map((src, index) => (
                <button
                  type="button"
                  className={`galleryButton ${gallerySpan(index)}`}
                  onClick={() => openLightbox(index)}
                  aria-label={`${brandName} photo ${index + 1}`}
                  key={src}
                >
                  <img
                    src={src}
                    alt={`${brandName} photo ${index + 1}`}
                    className="galleryImage"
                  />
                </button>
              ))}
            </div>
            <div className="ctaRow centerRow" style={{ marginTop: "2.5rem" }}>
              <h3 className="lifeLine">{t("life-line")}</h3>
            </div>
            <div className="ctaRow centerRow">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <CustomButton sx={ctaButtonSx}>{t("life-cta")}</CustomButton>
              </a>
            </div>

            <StyledLightboxModal
              open={activeImageIndex !== null}
              onClose={closeLightbox}
              aria-label="SAWA gallery"
            >
              <div className="lightboxOverlay" onClick={closeLightbox}>
                <IconButton
                  className="lightboxNav left"
                  onClick={(event) => {
                    event.stopPropagation();
                    showPrev();
                  }}
                  aria-label="Previous image"
                >
                  <NavigateBefore />
                </IconButton>
                <div
                  className="lightboxContent"
                  onClick={(event) => event.stopPropagation()}
                >
                  <IconButton
                    className="lightboxClose"
                    onClick={closeLightbox}
                    aria-label="Close gallery"
                  >
                    <Close />
                  </IconButton>
                  {activeImageIndex !== null && (
                    <img
                      src={gallery[activeImageIndex]}
                      alt={`${brandName} photo ${activeImageIndex + 1}`}
                      className="lightboxImage"
                    />
                  )}
                </div>
                <IconButton
                  className="lightboxNav right"
                  onClick={(event) => {
                    event.stopPropagation();
                    showNext();
                  }}
                  aria-label="Next image"
                >
                  <NavigateNext />
                </IconButton>
              </div>
            </StyledLightboxModal>
          </div>
        </section>

        {/* SAWA Subjects */}
        <section className="band bandWhite">
          <div className="bandInner">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <img
                  src={`${IMG}/subjects.webp`}
                  alt={t("subjects-h2")}
                  className="subjectsImage"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <h2>{t("subjects-h2")}</h2>
                <ul className="bulletList">
                  {subjectKeys.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </Grid>
            </Grid>
          </div>
        </section>

        {/* SAWA Programme Plan */}
        <section className="band planBand">
          <div className="bandInner">
            <h2 className="centerHeading planHeading">{t("plan-h2")}</h2>
            <Grid container spacing={2} className="planGrid">
              {phases.map((phase) => (
                <Grid item xs={12} sm={6} md={3} key={phase.num}>
                  <div className="planCard">
                    <span className="planNum">{phase.num}</span>
                    <span className="planDuration">{phase.duration}</span>
                    <h3 className="planTitle">{phase.title}</h3>
                    <p className="planDesc">{phase.desc}</p>
                    <hr className="planDivider" />
                    <p className="planAmount">{phase.allowance}</p>
                    <p className="planAllowanceLabel">{phase.label}</p>
                  </div>
                </Grid>
              ))}
            </Grid>
            <p className="planNote">{t("plan-note")}</p>
            <div className="homeVisit">
              <p className="homeVisitLabel">
                <Payments className="homeVisitIcon" />
                {t("plan-homevisit-label")}
              </p>
              <div className="homeVisitRows">
                {[t("plan-homevisit-1"), t("plan-homevisit-2")].map((row, i) => {
                  const ci = row.search(/[:：]/);
                  const region = ci >= 0 ? row.slice(0, ci) : row;
                  const amount = ci >= 0 ? row.slice(ci + 1).trim() : "";
                  return (
                    <span className="homeVisitRow" key={i}>
                      <span className="hvRegion">{region}</span>
                      <span className="hvAmount">{amount}</span>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="ctaRow centerRow">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <CustomButton sx={ctaButtonSx}>{t("plan-cta")}</CustomButton>
              </a>
            </div>
          </div>
        </section>

        {/* SAWA Participant Criteria */}
        <section className="band bandPeach">
          <div className="bandInner">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <h2 className="accentHeading">{t("criteria-h2")}</h2>
                <ul className="bulletList">
                  {criteria.map((c) => (
                    <li key={c.key}>
                      {t(c.key)}
                      {"note" in c && c.note ? (
                        <span className="criteriaNote"> {t(c.note)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </Grid>
              <Grid item xs={12} md={6}>
                <img
                  src={`${IMG}/criteria.webp`}
                  alt={t("criteria-h2")}
                  className="sideImage"
                />
              </Grid>
            </Grid>
          </div>
        </section>

        {/* SAWA In The Media */}
        <section className="contentSection mediaSection">
          <h2 className="centerHeading">{t("media-h2")}</h2>
          <div className="pressGrid">
            {press.map((item) => (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="pressItem"
                key={item.name}
              >
                <img src={item.logo} alt={item.name} className="pressLogo" />
              </a>
            ))}
          </div>
        </section>

        {/* Final CTA band */}
        <section className="finalBand">
          <h2>{t("cta-h2")}</h2>
          <div className="finalChecks">
            {ctaTicks.map((key) => (
              <div className="finalCheckItem" key={key}>
                <CheckCircle className="finalCheckIcon" />
                <span>{t(key)}</span>
              </div>
            ))}
          </div>
          <div className="ctaRow centerRow">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton sx={ctaButtonSx}>{t("cta-final")}</CustomButton>
            </a>
          </div>
        </section>
      </Root>
    </AppContainer>
  );
};

const PLAN_BAND = "#A54525";
const EARTH_RED = "#CC5803";

const Root = styled("div")(({ theme }) => ({
  marginBottom: "3rem",
  "& h2": {
    fontSize: "2rem",
  },
  "& .centerHeading": {
    textAlign: "center",
    marginBottom: "2.5rem",
  },
  "& .accentHeading": {
    color: EARTH_RED,
  },
  "& .hero": {
    textAlign: "left",
    marginBottom: "120px",
    paddingTop: "3rem",
  },
  "& .eyebrow": {
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    margin: "0 0 0.75rem",
  },
  "& .heroBrand": {
    fontSize: "2.2rem",
    fontWeight: 800,
    lineHeight: 1.15,
    margin: "0 0 0.75rem",
    color: theme.palette.primary.dark,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  "& .heroTags": {
    listStyle: "none",
    padding: 0,
    margin: "2rem 0",
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem 1.5rem",
    "& li": {
      display: "flex",
      alignItems: "center",
      gap: "0.35rem",
      fontStyle: "italic",
      fontWeight: 600,
      fontSize: "0.95rem",
      color: theme.palette.primary.dark,
    },
  },
  "& .heroTagIcon": {
    color: EARTH_RED,
    fontSize: "1.1rem",
  },
  "& .heroTitle": {
    marginTop: "2.5rem",
    fontSize: "1.8rem",
    fontWeight: 600,
    lineHeight: 1.1,
    [theme.breakpoints.down("md")]: {
      fontSize: "2.8rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.3rem",
    },
  },
  "& .heroAccent": {
    color: EARTH_RED,
  },
  "& .heroIntro": {
    fontSize: "1rem",
    lineHeight: 1.8,
  },
  "& .heroImage": {
    width: "100%",
    maxWidth: "400px",
    borderRadius: "1rem",
    display: "block",
    objectFit: "cover",
    aspectRatio: "3 / 4",
    margin: "0 auto",
  },
  "& .contentSection": {
    marginBottom: "120px",
  },
  "& .mediaSection": {
    marginTop: "120px",
  },
  // Full-bleed band: breaks out of the 1200px .main container to span the full
  // viewport width. Pair with a bg modifier (.bandWhite) or .planBand.
  "& .band": {
    width: "100vw",
    marginLeft: "calc(50% - 50vw)",
    marginRight: "calc(50% - 50vw)",
    padding: "90px 0",
  },
  "& .bandWhite": {
    backgroundColor: "#ffffff",
  },
  "& .bandPeach": {
    backgroundColor: "#f3e4d0",
  },
  "& .bandInner": {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 3rem",
    [theme.breakpoints.down("sm")]: {
      padding: "0 1.5rem",
    },
  },
  "& .ctaRow": {
    display: "flex",
    gap: "2rem",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: "2.5rem",
  },
  "& .centerRow": {
    justifyContent: "center",
  },
  "& .urgencyText": {
    fontWeight: 700,
    textAlign: "center",
    marginTop: "3rem",
  },
  // Why benefit cards
  "& .whyGridItem": {
    [theme.breakpoints.up("md")]: {
      flexBasis: "33.333%",
      maxWidth: "33.333%",
    },
  },
  "& .whyCard": {
    padding: "1rem 2rem",
    borderRadius: "1rem",
    boxShadow: "none",
    border: `1px solid ${EARTH_RED}14`,
    backgroundColor: `${EARTH_RED}0F`,
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    gap: "1.25rem",
    minHeight: "100px",
    "& h3": {
      fontSize: "1rem",
      lineHeight: 1.35,
      margin: 0,
      fontWeight: 700,
    },
  },
  "& .whyIcon": {
    fontSize: "2.4rem",
    color: EARTH_RED,
    flexShrink: 0,
  },
  // Gallery
  "& .galleryGrid": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridAutoRows: "150px",
    gridAutoFlow: "dense",
    gap: "8px",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(4, 1fr)",
      gridAutoRows: "180px",
    },
  },
  "& .spanWide": {
    gridColumn: "span 2",
  },
  "& .spanTall": {
    gridRow: "span 2",
  },
  "& .galleryButton": {
    border: 0,
    background: "transparent",
    padding: 0,
    display: "block",
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  "& .galleryImage": {
    width: "100%",
    height: "100%",
    borderRadius: ".5rem",
    display: "block",
    objectFit: "cover",
  },
  "& .lifeLine": {
    textAlign: "center",
    fontSize: "1.5rem",
    margin: 0,
  },
  // Side-by-side image sections
  "& .sideImage": {
    width: "100%",
    maxWidth: "520px",
    borderRadius: "1rem",
    display: "block",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    margin: "0 auto",
  },
  "& .subjectsImage": {
    width: "100%",
    maxWidth: "440px",
    maxHeight: "535px",
    borderRadius: "1rem",
    display: "block",
    aspectRatio: "3 / 4",
    objectFit: "cover",
    margin: "0 auto",
  },
  "& .bulletList": {
    marginTop: ".5rem",
    paddingLeft: "1.5rem",
    "& li": {
      marginBottom: ".5rem",
      lineHeight: 1.5,
    },
  },
  "& .criteriaNote": {
    display: "block",
    fontSize: ".9rem",
    fontStyle: "italic",
    opacity: 0.8,
    marginTop: ".15rem",
  },
  // Programme Plan band
  "& .planBand": {
    backgroundColor: PLAN_BAND,
    color: "#FFFFFF",
  },
  "& .planHeading": {
    color: "#FFFFFF",
  },
  "& .planCard": {
    position: "relative",
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: "0.75rem",
    padding: "2.75rem 1.25rem 1.5rem",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: ".5rem",
  },
  "& .planNum": {
    position: "absolute",
    top: "-16px",
    left: "1.25rem",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.dark,
    borderRadius: "8px",
    padding: "0.2rem 0.6rem",
    fontSize: "1.25rem",
    fontWeight: 800,
    lineHeight: 1.15,
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  "& .planDuration": {
    fontSize: ".8rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  "& .planTitle": {
    fontSize: "1.35rem",
    fontWeight: 700,
    margin: ".25rem 0 0",
  },
  "& .planDesc": {
    fontSize: ".9rem",
    lineHeight: 1.45,
    margin: 0,
    opacity: 0.9,
    flexGrow: 1,
  },
  "& .planDivider": {
    border: "none",
    borderTop: "1px solid rgba(255,255,255,0.25)",
    width: "100%",
    margin: ".85rem 0 .35rem",
  },
  "& .planAmount": {
    fontWeight: 800,
    fontSize: "1.7rem",
    lineHeight: 1.1,
    margin: 0,
    color: theme.palette.primary.main,
  },
  "& .planAllowanceLabel": {
    fontSize: ".8rem",
    margin: ".15rem 0 0",
    opacity: 0.85,
  },
  "& .planNote": {
    fontSize: ".85rem",
    fontStyle: "italic",
    textAlign: "center",
    marginTop: "1.5rem",
    opacity: 0.9,
  },
  "& .homeVisit": {
    textAlign: "center",
    marginTop: "2rem",
    "& .homeVisitLabel": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: ".4rem",
      fontWeight: 700,
      fontSize: "1.1rem",
      color: theme.palette.primary.main,
      margin: 0,
    },
    "& .homeVisitIcon": {
      fontSize: "1.4rem",
    },
    "& .homeVisitRows": {
      display: "flex",
      gap: "1rem 1.5rem",
      flexWrap: "wrap",
      justifyContent: "center",
      marginTop: "1rem",
    },
    "& .homeVisitRow": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "1.5rem",
      backgroundColor: "rgba(255,255,255,0.12)",
      borderRadius: "2rem",
      padding: ".8rem 1.5rem",
      flex: "1 1 340px",
      maxWidth: "480px",
    },
    "& .hvRegion": {
      fontWeight: 600,
    },
    "& .hvAmount": {
      fontWeight: 700,
      color: theme.palette.primary.main,
    },
  },
  // Press
  "& .pressGrid": {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "2rem",
    alignItems: "center",
    justifyItems: "center",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    },
  },
  "& .pressItem": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "64px",
  },
  "& .pressLogo": {
    maxHeight: "48px",
    maxWidth: "150px",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    mixBlendMode: "multiply",
    filter: "contrast(1.1)",
  },
  // Final dark band
  "& .finalBand": {
    backgroundColor: "#1c1c1c",
    color: "#FFFFFF",
    borderRadius: "1rem",
    padding: "70px 2rem",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2rem",
    marginBottom: 0,
    "& h2": {
      margin: 0,
    },
    "& .ctaRow": {
      marginTop: 0,
    },
  },
  "& .finalChecks": {
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem 2.5rem",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .finalCheckItem": {
    display: "flex",
    gap: ".6rem",
    alignItems: "center",
  },
  "& .finalCheckIcon": {
    color: theme.palette.primary.main,
    fontSize: "1.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    "& .planBand, & .finalBand": {
      padding: "40px 1.25rem",
    },
  },
}));

const StyledLightboxModal = styled(Modal)(({ theme }) => ({
  "& .lightboxOverlay": {
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: ".8rem",
    padding: "1rem",
    boxSizing: "border-box",
  },
  "& .lightboxContent": {
    position: "relative",
    maxWidth: "min(92vw, 980px)",
    maxHeight: "92vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .lightboxImage": {
    maxWidth: "100%",
    maxHeight: "92vh",
    width: "auto",
    height: "auto",
    borderRadius: ".5rem",
    display: "block",
  },
  "& .lightboxClose": {
    position: "absolute",
    top: ".5rem",
    right: ".5rem",
    zIndex: 2,
    color: "#FFFFFF",
    backgroundColor: "rgba(0,0,0,.45)",
    "&:hover": { backgroundColor: "rgba(0,0,0,.65)" },
  },
  "& .lightboxNav": {
    color: "#FFFFFF",
    backgroundColor: "rgba(0,0,0,.35)",
    "&:hover": { backgroundColor: "rgba(0,0,0,.55)" },
    "& svg": { fontSize: "2rem" },
  },
  [theme.breakpoints.down("sm")]: {
    "& .lightboxOverlay": { gap: ".3rem", padding: ".6rem" },
    "& .lightboxNav": { "& svg": { fontSize: "1.5rem" } },
  },
}));

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await cmsPageProps(locale, ["sawa"])),
  },
  revalidate: 60,
});

export default SawaPage;
